import React, { useState } from 'react'
import { Alert, AutoComplete, Button, Card, Col, DatePicker, Row, Select, Typography, message } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import moment from 'moment'
import felContingencySrc from './felContingencySrc'
import Utils from '../../utils/Utils'

const { Option } = Select
const { RangePicker } = DatePicker
const { Title } = Typography

export const FelContingencyResumen = () => {
  const [stores, setStores] = useState([])
  const [storeId, setStoreId] = useState(null)
  const [rango, setRango] = useState(null)
  const [loading, setLoading] = useState(false)
  const [resumen, setResumen] = useState(null)
  const [motivo, setMotivo] = useState('')

  const onSearchStore = val => {
    const excludeChars = Utils.excludedCharsContent(val)
    Utils.getStoreOption(val || '', excludeChars, 'desc')
      .then(response => {
        setStores(response.data.data || [])
      })
      .catch(err => {
        console.log(err)
        message.error('No se ha podido cargar la lista de tiendas')
      })
  }

  const buscar = () => {
    if (!storeId) {
      message.warning('Elegí una tienda primero')
      return
    }

    setLoading(true)
    // El usuario solo elige fecha (sin hora) para no confundir — igual
    // filtramos el día completo: desde las 00:00:00 hasta las 23:59:59.
    const from = rango?.[0] ? rango[0].format('YYYY-MM-DD') + ' 00:00:00' : null
    const to = rango?.[1] ? rango[1].format('YYYY-MM-DD') + ' 23:59:59' : null

    felContingencySrc
      .resumen(storeId, from, to)
      .then(response => {
        setResumen(response.data)
        setLoading(false)
      })
      .catch(err => {
        console.log(err)
        message.error('No se ha podido armar el resumen')
        setLoading(false)
      })
  }

  const formatearFecha = fecha => (fecha ? moment(fecha).format('DD/MM/YYYY HH:mm:ss') : '-')

  const copiarResumen = () => {
    const texto = [
      'Aviso de Contingencia — Factura Electrónica en Línea (FEL)',
      `Número de establecimiento: ${resumen.store.codigo_establecimiento ?? '-'}`,
      `Tienda: ${resumen.store.name}`,
      `Fecha y hora de inicio: ${formatearFecha(resumen.fecha_inicio)}`,
      `Fecha y hora de fin: ${resumen.en_curso ? 'Contingencia aún en curso — no cerrada' : formatearFecha(resumen.fecha_fin)}`,
      `Cantidad de documentos emitidos en contingencia: ${resumen.cantidad_documentos}`,
      `Motivo: ${motivo || '(completar)'}`,
    ].join('\n')

    navigator.clipboard
      .writeText(texto)
      .then(() => message.success('Resumen copiado — pegalo en la Agencia Virtual SAT o en el correo a efactura@sat.gob.gt'))
      .catch(() => message.error('No se pudo copiar automáticamente, seleccioná el texto manualmente'))
  }

  return (
    <>
      <Row className={'section-space-field'}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Alert
            type="info"
            showIcon
            message="Estos 5 datos son los que pide la SAT en su Agencia Virtual, apartado “Aviso de Contingencia” (o por correo a efactura@sat.gob.gt), al cerrar un período de contingencia."
          />
        </Col>
      </Row>
      <Row gutter={16} className={'section-space-field'} align="bottom">
        <Col xs={24} sm={24} md={8} lg={8}>
          <div className={'title-space-field'}>Tienda*</div>
          <Select
            showSearch
            optionFilterProp="children"
            className={'single-select'}
            placeholder={'Buscar tienda'}
            size={'large'}
            style={{ width: '100%' }}
            value={storeId}
            onChange={setStoreId}
            onSearch={onSearchStore}
            onFocus={() => onSearchStore('')}
            getPopupContainer={trigger => trigger.parentNode}
          >
            {stores.map(data => (
              <Option key={data.id} value={data.id}>
                {data.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={10} lg={10}>
          <div className={'title-space-field'}>Rango de fechas (opcional)</div>
          <RangePicker
            style={{ width: '100%' }}
            size={'large'}
            format={'YYYY-MM-DD'}
            value={rango}
            onChange={setRango}
          />
        </Col>
        <Col xs={24} sm={24} md={6} lg={6}>
          <Button className="title-tendero new-button" style={{ width: '100%' }} loading={loading} onClick={buscar}>
            Armar resumen
          </Button>
        </Col>
      </Row>

      {resumen && (
        <Row className={'section-space-field'}>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Card className={'card-border-radius margin-top-15'}>
              <Title level={4}>{`Resumen — ${resumen.store.name}`}</Title>
              {resumen.en_curso && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Esta tienda todavía tiene contingencias pendientes en el rango elegido — SAT pide avisar recién cuando la contingencia se cierra, no antes."
                />
              )}
              <p><strong>{'Número de establecimiento: '}</strong>{resumen.store.codigo_establecimiento ?? '-'}</p>
              <p><strong>{'Fecha y hora de inicio: '}</strong>{formatearFecha(resumen.fecha_inicio)}</p>
              <p><strong>{'Fecha y hora de fin: '}</strong>{resumen.en_curso ? 'Contingencia aún en curso' : formatearFecha(resumen.fecha_fin)}</p>
              <p><strong>{'Cantidad de documentos emitidos en contingencia: '}</strong>{resumen.cantidad_documentos}</p>
              <div className={'section-space-field'}>
                <div className={'title-space-field'}>Motivo</div>
                <AutoComplete
                  style={{ width: '100%' }}
                  size={'large'}
                  placeholder={'Elegí o escribí el motivo'}
                  value={motivo}
                  onChange={setMotivo}
                  getPopupContainer={trigger => trigger.parentNode}
                  options={[
                    { value: 'Falla de conectividad a internet' },
                    { value: 'Falla del servicio del Certificador (Digifact)' },
                    { value: 'Falla eléctrica' },
                  ]}
                />
              </div>
              <div className="text-right" style={{ marginTop: 16 }}>
                <Button icon={<CopyOutlined />} className="title-tendero new-button" onClick={copiarResumen} disabled={resumen.cantidad_documentos === 0}>
                  Copiar resumen
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </>
  )
}
